import { useEffect, useState } from "react"
import { PageHeader } from "@/widgets/page-header"
import { Modal } from "@/shared/ui/modal"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import {
    getSocialPrograms,
    createFoundationProgram,
    deleteSocialProgram,
    getSocialProgramCategories,
    getSocialProgramRegions,
    ISocialProgram,
    ISocialProgramCategory,
    ISocialProgramRegion,
} from "@/shared/api/social-programs"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { useNavigate } from "react-router-dom"

type ProgramTypeFilter = 'all' | 'user' | 'foundation'

const TYPE_LABELS: Record<ProgramTypeFilter, string> = {
    all: 'Все',
    user: 'Пользовательские',
    foundation: 'Фонды',
}

const emptyCreateForm = {
    name: '',
    description: '',
    category_id: '',
    region_id: '',
    logo: null as File | null,
}

export const SocialProgramsPage = () => {
    const navigate = useNavigate()

    const [programs, setPrograms] = useState<ISocialProgram[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [typeFilter, setTypeFilter] = useState<ProgramTypeFilter>('all')

    const [categories, setCategories] = useState<ISocialProgramCategory[]>([])
    const [regions, setRegions] = useState<ISocialProgramRegion[]>([])

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [createForm, setCreateForm] = useState(emptyCreateForm)
    const [createLoading, setCreateLoading] = useState(false)

    const [deletingId, setDeletingId] = useState<string | null>(null)

    const fetchPrograms = async () => {
        setIsLoading(true)
        try {
            const type = typeFilter === 'all' ? undefined : typeFilter
            const res = await getSocialPrograms(type)
            setPrograms(res.data)
        } catch {
            notify.error('Не удалось загрузить программы')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchPrograms()
    }, [typeFilter])

    useEffect(() => {
        getSocialProgramCategories().then(r => setCategories(r.data)).catch(() => {})
        getSocialProgramRegions().then(r => setRegions(r.data)).catch(() => {})
    }, [])

    const handleDelete = async () => {
        if (!deletingId) return
        await deleteSocialProgram(deletingId)
        notify.success('Программа удалена')
        fetchPrograms()
    }

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!createForm.logo) {
            notify.error('Выберите логотип')
            return
        }
        setCreateLoading(true)
        try {
            const fd = new FormData()
            fd.append('name', createForm.name)
            fd.append('description', createForm.description)
            if (createForm.category_id) fd.append('category_id', createForm.category_id)
            if (createForm.region_id) fd.append('region_id', createForm.region_id)
            fd.append('logo', createForm.logo)
            await createFoundationProgram(fd)
            notify.success('Программа создана')
            setIsCreateOpen(false)
            setCreateForm(emptyCreateForm)
            fetchPrograms()
        } catch (err) {
            notify.error(getErrorMessage(err))
        } finally {
            setCreateLoading(false)
        }
    }

    return (
        <div className="_page_container">
            <PageHeader title="Социальные программы" />
            <div className="flexbox">

                <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                        {(['all', 'user', 'foundation'] as ProgramTypeFilter[]).map(t => (
                            <button
                                key={t}
                                onClick={() => setTypeFilter(t)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                                    typeFilter === t
                                        ? 'bg-color-dark text-white'
                                        : 'bg-white text-color-dark border border-color-dark'
                                }`}
                            >
                                {TYPE_LABELS[t]}
                            </button>
                        ))}
                    </div>
                    <button
                        className="form_handle_button !w-auto px-4"
                        onClick={() => setIsCreateOpen(true)}
                    >
                        + Создать фонд
                    </button>
                </div>

                {isLoading && <div className="info_panel"><Loader /></div>}
                {!isLoading && programs.length === 0 && <NoTableData label="Нет программ" />}
                {!isLoading && programs.length > 0 && (
                    <div className="grid grid-cols-12 gap-4">
                        {programs.map(program => (
                            <div
                                key={program.id}
                                className="col-span-12 screen:col-span-6 box cursor-pointer hover:bg-gray-50 transition"
                                onClick={() => navigate(`/social-programs/${program.id}`)}
                            >
                                <div className="flex gap-3 items-start">
                                    {program.logo ? (
                                        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border">
                                            <img src={program.logo} className="w-full h-full object-contain" alt="" />
                                        </div>
                                    ) : (
                                        <div className="w-12 h-12 rounded-xl shrink-0 border bg-gray-100 flex items-center justify-center text-gray-400 text-xl font-bold">
                                            {program.name?.[0] ?? '?'}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-base leading-tight truncate">{program.name}</div>
                                        <div className="text-sm text-gray-500 mt-0.5 line-clamp-2">{program.description}</div>
                                        <div className="flex items-center gap-3 mt-2">
                                            <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${
                                                program.type === 'foundation'
                                                    ? 'bg-color-primary/10 text-color-primary'
                                                    : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {program.type === 'foundation' ? 'Фонд' : 'Пользовательская'}
                                            </span>
                                            <span className="text-xs text-gray-400">{program.total_users} уч.</span>
                                        </div>
                                    </div>
                                    {program.owner_user_id === null && (
                                        <button
                                            className="shrink-0 text-color-danger text-sm font-semibold px-3 py-1 border border-color-danger rounded-full"
                                            onClick={e => { e.stopPropagation(); setDeletingId(program.id) }}
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={!!deletingId}
                title="Удалить программу"
                message="Программа будет удалена без возможности восстановления. Продолжить?"
                confirmText="Удалить"
                onConfirm={handleDelete}
                onCancel={() => setDeletingId(null)}
            />

            <Modal
                isOpen={isCreateOpen}
                title="Создать программу-фонд"
                onRequestClose={() => { setIsCreateOpen(false); setCreateForm(emptyCreateForm) }}
            >
                <form onSubmit={handleCreate} className="flex flex-col gap-3">
                    <div className={`flex flex-col gap-2 pb-[10px] border-b-[1.5px] border-[#B9C9C6]`}>
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Название</label>
                        <input
                            className="w-full outline-none text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                            placeholder="Введите название"
                            value={createForm.name}
                            onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                            required
                        />
                    </div>
                    <div className={`flex flex-col gap-2 pb-[10px] border-b-[1.5px] border-[#B9C9C6]`}>
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Описание</label>
                        <textarea
                            className="w-full outline-none resize-none text-[13px] [&::placeholder]:text-color-dark/70 text-color-dark"
                            placeholder="Введите описание"
                            rows={3}
                            maxLength={2048}
                            value={createForm.description}
                            onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Категория</label>
                        <select
                            className="input_field"
                            value={createForm.category_id}
                            onChange={e => setCreateForm(f => ({ ...f, category_id: e.target.value }))}
                        >
                            <option value="">Не выбрана</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Регион</label>
                        <select
                            className="input_field"
                            value={createForm.region_id}
                            onChange={e => setCreateForm(f => ({ ...f, region_id: e.target.value }))}
                        >
                            <option value="">Не выбран</option>
                            {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="font-semibold uppercase text-[12px] text-[#828282]">Логотип *</label>
                        <input
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={e => setCreateForm(f => ({ ...f, logo: e.target.files?.[0] ?? null }))}
                            required
                        />
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button
                            type="button"
                            className="form_cancel_button"
                            onClick={() => { setIsCreateOpen(false); setCreateForm(emptyCreateForm) }}
                        >
                            Отмена
                        </button>
                        <button type="submit" className="form_handle_button" disabled={createLoading}>
                            {createLoading ? 'Создание...' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    )
}
